import React, { useState, useEffect, useContext } from 'react';
import { animate, motion, useMotionValue, useTransform } from 'framer-motion';
import { Alert, Box, Button, Card, CardContent, CardCover, Chip, CircularProgress, IconButton, Stack, Typography } from '@mui/joy';

import ReportRoundedIcon from '@mui/icons-material/ReportRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import AddIcon from '@mui/icons-material/Add';

import PlaceIcon from '@mui/icons-material/Place';
import { APIGetProfiles, APIGetUserTariffInfo, APILikeProfile, APISuperLikeProfile } from '../../api';
import { LayoutContext } from '../../context';
import CompatibilityBadge from './CompatibilityBadge';
import PassedQuizzesChips from './PassedQuizzesChips';
import CompatibilityModal from './CompatibilityModal';
import SmartMatchFilter from './SmartMatchFilter';
import AiReasonChip from './AiReasonChip';
import AdDeckCard from '../Ads/user/AdDeckCard';
import useAdSlot from '../Ads/user/useAdSlot';
import { useAds } from '../Ads/user/AdsContext';
import EmptyState from '../ui/EmptyState';
import StyleRoundedIcon from '@mui/icons-material/StyleRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import HideImageOutlinedIcon from '@mui/icons-material/HideImageOutlined';
import './SwipeCards.css';


// Создаем анимированную версию компонента Card
const MotionCard = motion(Card);


// ===== ЗАПОМНЕННЫЕ НАСТРОЙКИ ПОДБОРА =====
const MATCH_PREFS_KEY = 'lovirds_match_prefs';

function loadMatchPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(MATCH_PREFS_KEY) || 'null');
    return {
      sortByCompat: Boolean(raw?.sortByCompat),
      minCompat: Number.isFinite(raw?.minCompat) ? raw.minCompat : 0,
    };
  } catch (_e) {
    return { sortByCompat: false, minCompat: 0 };
  }
}

function saveMatchPrefs(prefs) {
  try { localStorage.setItem(MATCH_PREFS_KEY, JSON.stringify(prefs)); }
  catch (_e) { /* приватный режим — не критично */ }
}

function ProfilePhoto({ src, name }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  return (
    <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(145deg, #f5f3f8, #e8e5ee)' }}>
      <Box role={!src || failed ? 'img' : undefined} aria-label={!src || failed ? 'Фото отсутствует' : undefined} aria-hidden={src && !failed ? true : undefined} sx={{
        position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', color: '#b3adbF',
      }}>
        <HideImageOutlinedIcon sx={{ fontSize: { xs: 70, sm: 86 }, color: '#b3adbf', opacity: 0.7 }} />
      </Box>
      {src && !failed && (
        <img src={src} alt={name || 'Фото анкеты'} loading="eager" decoding="async"
          onError={() => setFailed(true)}
          style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
      )}
    </Box>
  );
}

const SwipeCards = () => {
  const { token, updateStatusListProfiles, setModalComplaint, currentUser, setPurchaseModalOpen, systemSettings } = useContext(LayoutContext);

  // Настройки (можно менять)
  const SWIPE_THRESHOLD = 60; // Порог свайпа в пикселях
  const ROTATION_ANGLE = 20; // Максимальный угол поворота карточки
  const RETURN_ANIMATION_DURATION = 0.8; // Длительность анимации возврата
  const CARD_TRANSITION = { 
    type: 'spring', 
    stiffness: 600, // Увеличили жесткость для более быстрого отклика
    damping: 30,
    duration: 0.01,
    restDelta: 0
  };

  const [ errorMessage, setErrorMessage ] = useState(null);

  const [ profiles, setProfiles ] = useState([]);
  const [ currentPage, setCurrentPage ] = useState(1);
  const [ totalPages, setTotalPages ] = useState(1);
  const [ isLoading, setIsLoading ] = useState(false);
  const [ currentIndex, setCurrentIndex ] = useState(0);
  const [ viewOnlyMode, setViewOnlyMode ] = useState(false);

  const [superLikesRemaining, setSuperLikesRemaining] = useState(5);

  // Настройки подбора переживают перезаход: раньше сбрасывались при каждом
  // открытии ленты, и человек каждый раз выставлял их заново
  const savedMatch = loadMatchPrefs();
  const [ sortByCompat, setSortByCompat ] = useState(savedMatch.sortByCompat);
  const [ minCompat, setMinCompat ] = useState(savedMatch.minCompat);
  const [ compatProfile, setCompatProfile ] = useState(null);

  // ===== РЕКЛАМА В КОЛОДЕ =====
  // Рекламная карточка НЕ подмешивается в profiles: иначе поедут
  // пагинация (profiles.length - currentIndex), предзагрузка изображений
  // и логика лайков. Она рендерится как верхняя карточка стопки,
  // а profiles и currentIndex остаются нетронутыми.
  const { resetSession } = useAds();
  const [ swipesSinceAd, setSwipesSinceAd ] = useState(0);
  const [ hasSeenAd, setHasSeenAd ] = useState(false);
  const [ adRule, setAdRule ] = useState({ first_at: 3, every_n_items: 6 });
  const adThreshold = Math.max(1, Number(hasSeenAd ? adRule.every_n_items : adRule.first_at) || 1);
  const adDue = swipesSinceAd >= adThreshold;
  const { ad: deckAd, placementInfo: deckPlacement } = useAdSlot('deck_card', {
    enabled: !errorMessage && adDue,
  });
  useEffect(() => {
    if (deckPlacement?.position_rule) {
      setAdRule(rule => ({ ...rule, ...deckPlacement.position_rule }));
    }
  }, [deckPlacement]);
  const adIsDue = adDue && !!deckAd;
  const closeDeckAd = () => {
    setHasSeenAd(true);
    setSwipesSinceAd(0);
  };

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-ROTATION_ANGLE, ROTATION_ANGLE]);
  const backgroundColor = useTransform(
    x,
    [-SWIPE_THRESHOLD, 0, SWIPE_THRESHOLD],
    ['rgba(255, 50, 50, 0.5)', 'rgba(255, 255, 255, 0)', 'rgba(50, 255, 50, 0.5)']
  );

  // Загрузка профилей
  const loadProfiles = async (page = 1, reset = false) => {
    if (isLoading) return;
    
    console.log('Загрузка профилей, страница:', page, 'reset:', reset);
    setIsLoading(true);
    try {
      const extra = {};
      if (sortByCompat) extra.sortBy = 'compatibility';
      if (minCompat > 0) extra.minCompatibility = minCompat;
      const response = await APIGetProfiles(token, page, 10, extra);
      
      // Устанавливаем флаг режима просмотра
      if (response.view_only_mode !== undefined) {
        setViewOnlyMode(response?.view_only_mode);
      }

      if (reset) {
        setProfiles(response.profiles);
      } else {
        setProfiles(prev => [...prev, ...response.profiles]);
      }
      setTotalPages(response.total_pages);
      setCurrentPage(page);

      // Если есть ошибка модерации
      if (response?.error) {
        setErrorMessage(response.error);
      }
    } catch (error) {
      if (error?.response?.data?.status) {
        setErrorMessage(error?.response?.data?.error);
        return
      }
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Обработчик открытия жалобы
  const handleOpenComplaint = (profile) => {
    setModalComplaint({ 
      open: true, 
      user: {
        id: profile.user_id,
        full_name: profile.name
      }
    });
  };

  // Первоначальная загрузка
  // useEffect(() => {
  //   loadProfiles(1, true); // true означает полный сброс
  // }, []);

  // Загрузка если обновить надо
  useEffect(() => {
    setCurrentIndex(0); // Сбрасываем на первую карточку
    setCurrentPage(0); // Сбрасываем пагинацию
    loadProfiles(1, true); // Загружаем с первой страницы
    // Новая сессия показа: без этого сервер будет считать креативы
    // уже показанными и отдаст пустоту после «начать заново»
    resetSession();
    setSwipesSinceAd(0);
    setHasSeenAd(false);
  }, [updateStatusListProfiles]);

  // Перезагрузка при изменении умного фильтра по совместимости
  useEffect(() => {
    setCurrentIndex(0);
    setCurrentPage(0);
    loadProfiles(1, true);
    saveMatchPrefs({ sortByCompat, minCompat });
    resetSession();
    setSwipesSinceAd(0);
    setHasSeenAd(false);
  }, [sortByCompat, minCompat]);

  // Проверка необходимости подгрузки новых профилей
  useEffect(() => {
    if (!errorMessage && profiles?.length - currentIndex <= 3 && currentPage < totalPages && !isLoading) {
      loadProfiles(currentPage + 1);
    }
  }, [currentIndex, profiles?.length, currentPage, totalPages, isLoading]);

  // Предзагрузка следующих фото не блокирует показ активной карточки.
  useEffect(() => {
    const images = profiles.slice(currentIndex + 1, currentIndex + 3)
      .map(profile => profile?.photo_id || profile?.photo_file_path)
      .filter(Boolean)
      .map(src => { const image = new Image(); image.src = src; return image; });
    return () => images.forEach(image => { image.onload = null; image.onerror = null; });
  }, [currentIndex, profiles]);

  // Обработка лайка
  const handleLike = async (profileId) => {
    if (viewOnlyMode) {
      console.log('Режим просмотра: лайки недоступны');
      return;
    }

    try {
      const response = await APILikeProfile(token, profileId);
      console.log('Лайк отправлен:', response);
      
      if (response.mutual_like) {
        console.log('Взаимный лайк!');
        // Можно показать уведомление о взаимном лайке
      }
    } catch (error) {
      console.error('Ошибка отправки лайка:', error);
    }
  };

  useEffect(() => {
    // При загрузке профиля получаем информацию о тарифе
    const loadUserTariffInfo = async () => {
      try {
        const tariffInfo = await APIGetUserTariffInfo(token);
        setSuperLikesRemaining(tariffInfo.tariff_info?.likes_remaining?.super_likes || 0);
      } catch (error) {
        console.error('Ошибка загрузки информации о тарифе:', error);
      }
    };
    
    loadUserTariffInfo();
  }, [token]);

  // Функция для обработки суперлайка
  const handleSuperLike = async (profileId) => {
    if (viewOnlyMode) {
      console.log('Режим просмотра: суперлайки недоступны');
      return;
    }

    if (superLikesRemaining <= 0) {
      console.log('Лимит суперлайков исчерпан');
      return;
    }

    try {
      const response = await APISuperLikeProfile(token, profileId, true);
      console.log('Суперлайк отправлен:', response);
      
      // Уменьшаем счетчик суперлайков
      setSuperLikesRemaining(prev => prev - 1);
      
      if (response.mutual_like) {
        console.log('Взаимный суперлайк!');
      }
    } catch (error) {
      console.error('Ошибка отправки суперлайка:', error);
    }
  };

  // Обработка дизлайка
  const handleDislike = async (profileId) => {
    if (viewOnlyMode) {
      console.log('Режим просмотра: действия недоступны');
      return;
    }

    // Здесь можно добавить логику для дизлайка, если нужно
    console.log('Дизлайк профиля:', profileId);
  };
  

  const handleDragEnd = async (_, info) => {
    const offset = info.offset.x;
    const velocity = info.velocity.x;

    if (Math.abs(offset) > SWIPE_THRESHOLD || Math.abs(velocity) > 500) {
      const direction = offset > 0 ? 'right' : 'left';
      await handleSwipe(direction);
    } else {
      animate(x, 0, CARD_TRANSITION);
    }
  };

  const handleSwipe = async (direction) => {
    const flyOutX = direction === 'right' ? 600 : -600;

    // Если сверху рекламная карточка — свайп её закрывает.
    // Ни лайка, ни дизлайка не отправляем и currentIndex не двигаем:
    // анкета под рекламой ещё не просмотрена.
    if (adIsDue) {
      await animate(x, flyOutX, { type: "spring", stiffness: 300, damping: 25 });
      closeDeckAd();
      x.set(0);
      return;
    }

    const currentProfile = profiles[currentIndex];

    if (!viewOnlyMode) {
      // Отправляем лайк/дизлайк
      if (direction === 'right') {
        await handleLike(currentProfile.id);
      } else {
        await handleDislike(currentProfile.id);
      }
    }

    // Быстрая анимация улетания текущей карточки
    await animate(x, flyOutX, {
      type: "spring",
      stiffness: 300,
      damping: 25,
      // duration: 0.01
    });

    // Мгновенное обновление позиций всех карточек
    setCurrentIndex(prev => prev + 1);
    setSwipesSinceAd(prev => prev + 1);
    x.set(0);
  };

  // Кнопки для лайка/дизлайка
  const handleLikeButton = () => {
    handleSwipe('right');
  };

  const handleDislikeButton = () => {
    handleSwipe('left');
  };

  const visibleProfiles = profiles.slice(currentIndex, currentIndex + 3);

  // Баннер режима просмотра
  const ViewOnlyBanner = () => (
    <Alert
      color="warning" 
      variant='outlined'
      startDecorator={<VisibilityRoundedIcon />}
      sx={{ mb: 2, borderRadius: 'xl' }}
    >
      <Typography level="body-xs" sx={{ p: 0, lineHeight: 1.2 }}>
        Ваш профиль на модерации. Вы можете просматривать анкеты, но лайки будут доступны после проверки.
      </Typography>
    </Alert>
  );

  if (errorMessage) {
    return (
      <Stack justifyContent="center" alignItems="center" height="100%">
        <Typography level="h4" color={'danger'} sx={{ textAlign: "center" }}>{errorMessage}</Typography>
      </Stack>
    );
  }

  if (profiles.length === 0 && !isLoading) {
    return (
      <Stack justifyContent="center" sx={{ height: '100%', py: 2 }}>
        <EmptyState
          icon={<StyleRoundedIcon />}
          title="Анкет пока нет"
          text="Мы показываем людей вашего города. Попробуйте расширить условия в фильтре подбора — например, диапазон возраста."
        />
      </Stack>
    );
  }

  if (currentIndex >= profiles.length && !isLoading) {
    return (
      <Stack justifyContent="center" sx={{ height: '100%', py: 2 }}>
        <EmptyState
          icon={<DoneAllRoundedIcon />}
          title="Вы просмотрели все анкеты"
          text="Новые появятся, как только кто-то зарегистрируется в вашем городе. А пока можно пройти круг заново."
          action={
            <button
              type="button"
              className="lvc-btn lvc-btn--primary"
              onClick={() => {
                setCurrentIndex(0); // Сбрасываем на первую карточку
                setCurrentPage(0); // Сбрасываем пагинацию
                loadProfiles(1, true); // Загружаем с первой страницы
              }}
            >
              Начать заново
            </button>
          }
        />
      </Stack>
    );
  }


  return (
    <Stack
      spacing={2}
      sx={{
        width: '100%', minWidth: 0, overflow: 'visible', pb: 2,
      }}
    >
      {/* Баннер режима просмотра */}
      {viewOnlyMode && <ViewOnlyBanner />}

      <SmartMatchFilter
        token={token}
        sortByCompat={sortByCompat}
        minCompat={minCompat}
        onChange={({ sortByCompat: s, minCompat: m }) => { setSortByCompat(s); setMinCompat(m); }}
      />

      <CompatibilityModal
        open={!!compatProfile}
        profile={compatProfile}
        token={token}
        onClose={() => setCompatProfile(null)}
      />

      {!isLoading && (
        <div className="lvc-swipe-scene">
          {/* Рекламная карточка кладётся поверх стопки как активная.
              Свайп по ней обрабатывается в handleSwipe отдельной веткой */}
          {adIsDue && (
            <AdDeckCard
              key={deckAd.token}
              ad={deckAd}
              onSkip={closeDeckAd}
              style={{
                width: 'calc(100% - 64px)',
                height: 'calc(100% - 80px)',
                zIndex: 100,
                x: x,
                rotate: rotate,
              }}
            />
          )}

          {visibleProfiles.map((profile, index) => {
            // Пока сверху реклама, анкеты не перетаскиваются
            const isActive = index === 0 && !adIsDue;
            const zIndex = visibleProfiles.length - index;
            const scale = 1 - index * 0.05;
            const yOffset = index * 10;

            return (
              <MotionCard
                key={profile.id}
                className='card'
                drag={isActive ? "x" : false}
                onDragEnd={isActive ? handleDragEnd : undefined}
                animate={{
                  y: yOffset,
                  scale,
                  transition: {
                    ...CARD_TRANSITION,
                    delay: 0 // Убрали задержку для фоновых карточек
                  }
                }}
                style={{
                  x: isActive ? x : 0,
                  rotate: isActive ? rotate : 0,
                  // backgroundColor: isActive ? backgroundColor : 'transparent',
                  zIndex,
                  position: 'absolute',
                  width: 'calc(100% - 64px)',
                  height: 'calc(100% - 80px)',
                  touchAction: 'pan-y',
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  borderRadius: 26,
                  border: '0',
                  boxShadow: isActive
                    ? '0 16px 32px rgba(96, 40, 160, 0.18)'
                    : '0 8px 20px rgba(96, 40, 160, 0.10)',
                  cursor: isActive ? 'grab' : 'default',
                }}
              >
                <CardCover className="card-content"
                  sx={{  
                    "::after": {
                      content: `""`,
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      backgroundColor: isActive ? backgroundColor : 'transparent',
                      mixBlendMode: "multiply"
                    }
                  }}
                >
                  <ProfilePhoto src={profile?.photo_id || profile?.photo_file_path} name={profile.name} />
                </CardCover>
                <CardCover
                  sx={{
                    background:
                      'linear-gradient(to top, rgba(0,0,0,0.4), rgba(0,0,0,0) 200px), linear-gradient(to top, rgba(0,0,0,0.8), rgba(0,0,0,0) 300px)',
                  }}
                />
                <CardContent sx={{ justifyContent: "space-between" }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ width: "100%" }}>
                    <Button startDecorator={<ReportRoundedIcon/>} size='sm' color='warning' variant='soft' sx={{ width: "fit-content" }}
                      onClick={() => handleOpenComplaint(profile)}
                    >
                      Пожаловаться
                    </Button>
                    {profile.compatibility != null && (
                      <CompatibilityBadge
                        value={profile.compatibility}
                        onClick={() => setCompatProfile(profile)}
                      />
                    )}
                  </Stack>
                  <Stack direction={"column"}>
                    <Chip startDecorator={<PlaceIcon/>} size="md" color='primary'>
                      {profile.city}
                    </Chip>
                    <Typography
                      level="h2"
                      textColor="#fff"
                      sx={{ fontWeight: 'lg', mt: 1 }}
                    >
                      {profile.name}, {profile.age}
                    </Typography>
                    <AiReasonChip reason={profile.ai_reason} />
                    <Stack direction={"row"} flexWrap={'wrap'} spacing={1} sx={{ mt: 1 }}>
                      {profile.hobby && profile.hobby.split(',').slice(0, 3).map((hobby, i) => (
                        <Chip key={i} size="sm">
                          {hobby.trim()}
                        </Chip>
                      ))}
                    </Stack>
                    <PassedQuizzesChips quizzes={profile.passed_quizzes} />
                  </Stack>
                </CardContent>
              </MotionCard>
            );
          })}
        </div>
      )}

      
      {/* Кнопки управления */}
      {!isLoading && (
        <Stack spacing={1.5} sx={{ position: 'relative', pb: 1.5 }}>
          <Typography level="body-xs" sx={{ textAlign: 'center', color: 'var(--lvc-ink-soft)' }}>
            Листайте карточку влево или вправо
          </Typography>

          {/* Круглые кнопки: «нет» — слева, суперлайк — крупный по центру,
              «нравится» — справа. Так же, как во всех приложениях знакомств */}
          <Stack
            direction="row"
            spacing={2.5}
            justifyContent="center"
            alignItems="center"
            sx={{ zIndex: 1000 }}
          >
            <IconButton
              onClick={handleDislikeButton}
              aria-label="Пропустить"
              sx={{
                width: 58, height: 58, borderRadius: '50%',
                backgroundColor: '#fff', color: '#ff4d6d',
                border: '1px solid rgba(255, 77, 109, .2)',
                boxShadow: '0 10px 24px rgba(96, 40, 160, .12)',
                transition: 'transform .18s',
                '&:hover': { backgroundColor: '#fff5f7', transform: 'translateY(-2px)' },
                '&:active': { transform: 'scale(.94)' },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 26 }} />
            </IconButton>

            {/* Суперлайк */}
            <IconButton
              onClick={() => {
                const currentProfile = profiles[currentIndex];
                if (currentProfile) {
                  handleSuperLike(currentProfile.id);
                  handleSwipe('right');
                }
              }}
              disabled={superLikesRemaining <= 0}
              aria-label="Суперлайк"
              sx={{
                width: 70, height: 70, borderRadius: '50%',
                background: 'var(--lvc-grad)',
                color: '#fff',
                boxShadow: '0 14px 30px rgba(180, 60, 240, .38)',
                opacity: superLikesRemaining <= 0 ? 0.45 : 1,
                transition: 'transform .18s, filter .2s',
                '&:hover': { filter: 'brightness(1.07)', transform: 'translateY(-2px)' },
                '&:active': { transform: 'scale(.94)' },
              }}
            >
              <StarRoundedIcon sx={{ fontSize: 32 }} />
            </IconButton>

            <IconButton
              onClick={handleLikeButton}
              aria-label="Нравится"
              sx={{
                width: 58, height: 58, borderRadius: '50%',
                backgroundColor: '#fff', color: '#18a558',
                border: '1px solid rgba(24, 165, 88, .2)',
                boxShadow: '0 10px 24px rgba(96, 40, 160, .12)',
                transition: 'transform .18s',
                '&:hover': { backgroundColor: '#f2fbf5', transform: 'translateY(-2px)' },
                '&:active': { transform: 'scale(.94)' },
              }}
            >
              <FavoriteBorderRoundedIcon sx={{ fontSize: 26 }} />
            </IconButton>
          </Stack>
          
          {/* Добавьте отображение оставшихся лайков с кнопкой покупки */}
          { systemSettings?.is_paid_mode && (
            <>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography level="body-sm">
                  Лайков: {currentUser?.tariff_info?.likes_remaining || 0}
                </Typography>
                <Button 
                  size="sm" 
                  variant="soft" 
                  color="neutral"
                  onClick={() => setPurchaseModalOpen(true)}
                  startDecorator={<AddIcon />}
                  endDecorator={<StarRoundedIcon />}
                >
                  Купить
                </Button>
              </Stack>

              {/* Отображение оставшихся суперлайков */}
              <Typography level="body-sm" sx={{ textAlign: 'center' }}>
                Суперлайков осталось: {superLikesRemaining}
              </Typography>
            </>
          )}
        </Stack>
      )}

      {!isLoading && superLikesRemaining <= 0 && (
        <Alert
          color="warning" 
          sx={{ mt: 2 }}
          endDecorator={
            <Button 
              size="sm" 
              variant="soft" 
              color="warning"
              onClick={() => setPurchaseModalOpen(true)}
              startDecorator={<AddIcon />}
            >
              Купить
            </Button>
          }
        >
          <Typography level="body-sm">
            Суперлайки закончились
          </Typography>
        </Alert>
      )}

      {isLoading && (
        <Stack justifyContent="center" alignItems="center" height="100%">
          {/* <Typography>Загрузка анкет...</Typography> */}
          <CircularProgress />
        </Stack>
      )}
    </Stack>
  );
};

export default SwipeCards;
